import SegmentedButtons, { SegmentedButtonItem } from '3-components/SegmentedButtons/SegmentedButtons';
import SegmentedButtonField from '3-components/SegmentedButtons/SegmentedButtonsField/SegmentedButtonField';
import { useEffect, useState } from 'react';
import SmallMonthlyNudge from '3-components/Donations/SmallMonthlyNudge/SmallMonthlyNudge';
import ThankYouMessage from '3-components/ThankYouMessage/ThankYouMessage';

type FrequencyOption = {
    value: string;
    label: string;
    checked: boolean;
    disabled?: boolean;
}

interface FrequencyOptionsProps {
    fieldLabel: string;
    fieldName: string;
    options: FrequencyOption[];
    onChange: (value: string) => void;
    nudgeLabel?: string;
    monthlyNudgeLabel?: string;
    enableNudge?: boolean;
    selectedVal?: string;
}

const FrequencyOptions = (props: FrequencyOptionsProps): JSX.Element => {
    const {
        fieldLabel,
        fieldName,
        options,
        onChange,
        enableNudge = true,
        nudgeLabel = '',
        monthlyNudgeLabel = '',
        selectedVal,
    } = props;

    // Track if we need to show the thank you message
    const [showMonthlyThankYou, setShowMonthlyThankYou] = useState(false);
    // Track if this is the first render/initialization
    const [isInitialRender, setIsInitialRender] = useState(true);

    // Determine initial selection and if monthly is the default
    const initialMonthlyDefault = options.find(option => {
        return option.value === 'monthly' && option.checked;
    }
    ) !== undefined;

    const [selectedValue, setSelectedValue] = useState(() => {
        if (selectedVal) {
            return selectedVal;
        }
        return options.find(option => {
            return option.checked;
        })?.value;
    });

    useEffect(() => {
        if (selectedVal) {
            setSelectedValue(selectedVal);
            onChange(selectedVal);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedVal]);

    // After first render, mark initialization complete
    useEffect(() => {
        if (isInitialRender) {
            setIsInitialRender(false);
        }
    }, [isInitialRender]);

    const icon = {
        once: 'eye-open',
        monthly: 'volunteer',
    };

    // Logic to determine when to show thank you message:
    // 1. Monthly is the default: Only show thank you after user has clicked once then monthly
    // 2. Once is the default: Show thank you on first click of monthly
    const isMonthlySelected = selectedValue === 'monthly';
    const shouldShowThankYou = isMonthlySelected && (
        // Either show monthly thank you is explicitly set
        showMonthlyThankYou ||
        // Or monthly is selected but was not the default option (once was default)
        (!initialMonthlyDefault && !isInitialRender)
    );

    return (
        <div className="frequency-field-wrapper">
            <SegmentedButtonField
                label={fieldLabel}
            >
                <SegmentedButtons>
                    {options.map(option => {
                        const { label, value, disabled = false } = option;
                        return (
                            <SegmentedButtonItem
                                label={label}
                                id={`frequency-${value}`}
                                name={fieldName}
                                value={value}
                                checked={value === selectedValue}
                                disabled={disabled}
                                alwaysShowIcon={true}
                                icon={icon[value]}
                                onChange={(): void => {
                                    // Keep track if we're switching from once to monthly
                                    const wasOnceSelected = selectedValue === 'once';

                                    // Set the new selection
                                    setSelectedValue(value);
                                    onChange(value);

                                    // If monthly is default and user clicks once then monthly
                                    if (initialMonthlyDefault && value === 'monthly' && wasOnceSelected) {
                                        setShowMonthlyThankYou(true);
                                    }
                                }}
                                key={`frequency-option-${option.value}`}
                            />
                        );
                    })}
                </SegmentedButtons>
            </SegmentedButtonField>

            {enableNudge && (
                isMonthlySelected ?
                    (shouldShowThankYou ?
                        <ThankYouMessage>{monthlyNudgeLabel}</ThankYouMessage> :
                        <SmallMonthlyNudge label={nudgeLabel} />) :
                    <SmallMonthlyNudge label={nudgeLabel} />
            )}
        </div>
    );
};

export default FrequencyOptions;
